const FAILED_STATUSES = new Set([
  "ota_error",
  "version_mismatch",
  "ota_failed_permanently",
  "failed",
  "cancelled",
]);

const TERMINAL_STATUSES = new Set([
  "success",
  ...FAILED_STATUSES,
]);

const KNOWN_LIVE_STATUSES = new Set([
  "requesting_url",
  "ota_task_executing",
  "ota_started",
  "downloading",
  "success",
  "ota_error",
  "version_mismatch",
  "ota_failed_permanently",
  "error",
]);

export { KNOWN_LIVE_STATUSES };

export const sortEventsByTimeDesc = (events = []) =>
  [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

export const DEVICE_LOG_STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "In progress" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
];

export const normalizeCampaignId = (id) =>
  id == null || id === "" ? null : String(id);

export const campaignIdsMatch = (a, b) =>
  normalizeCampaignId(a) === normalizeCampaignId(b);

export const deviceLogMatchesSearch = (row, search) => {
  if (!search) return true;
  const term = search.toLowerCase();
  return [row.srId, row.mqttDeviceId, row.deviceRole]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(term));
};

export const deviceLogMatchesStatusFilter = (row, statusFilter) => {
  if (!statusFilter || statusFilter === "all") return true;
  if (statusFilter === "success") return row.status === "success";
  if (statusFilter === "failed") return FAILED_STATUSES.has(row.status);
  if (statusFilter === "pending") return !TERMINAL_STATUSES.has(row.status);
  return row.status === statusFilter;
};

export const filterCampaignDevices = (devices, { search = "", statusFilter = "all" } = {}) =>
  (devices ?? []).filter(
    (row) =>
      deviceLogMatchesSearch(row, search) &&
      deviceLogMatchesStatusFilter(row, statusFilter),
  );

export const deviceLogMatchesEventSearch = (event, search) => {
  if (!search) return true;
  const term = search.toLowerCase();
  return [event.mqttDeviceId, event.srId, event.eventType, event.errorMessage]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(term));
};

export const buildCampaignDeviceBuckets = (devices = []) => {
  const succeeded = devices.filter((row) => row.status === "success");
  const failed = devices.filter((row) => FAILED_STATUSES.has(row.status));
  const pending = devices.filter((row) => !TERMINAL_STATUSES.has(row.status));

  return { devices, succeeded, failed, pending };
};

export const applyLivePayloadToDevice = (row, payload) => {
  const retriedAfterFailure =
    payload.status === "triggered" &&
    row.status &&
    FAILED_STATUSES.has(row.status);

  return {
    ...row,
    status: KNOWN_LIVE_STATUSES.has(payload.status)
      ? (payload.status ?? row.status)
      : row.status,
    progress: payload.percent ?? payload.progress ?? row.progress,
    speed: payload.speed ?? row.speed,
    downloaded: payload.downloaded ?? row.downloaded,
    total: payload.total ?? row.total,
    currentVersion: payload.currentVer ?? row.currentVersion,
    attempt: retriedAfterFailure
      ? (row.attempt ?? 1) + 1
      : row.attempt ?? 1,
    errorStep: payload.step ?? row.errorStep,
    errorMessage: payload.message ?? payload.err ?? row.errorMessage,
  };
};

export const buildLiveLogEvent = (row, payload) => {
  const status = payload.status;
  const isKnownStatus = KNOWN_LIVE_STATUSES.has(status);
  let eventType = "progress";
  if (status === "success") eventType = "success";
  else if (FAILED_STATUSES.has(status)) eventType = "failed";
  else if (status === "triggered") eventType = "triggered";
  else if (!isKnownStatus && payload.message) eventType = "info";

  return {
    id: `ws-${payload.mqttDeviceId}-${payload.receivedAt ?? Date.now()}`,
    mqttDeviceId: payload.mqttDeviceId,
    dbDeviceId: payload.dbDeviceId ?? row.dbDeviceId,
    srId: row.srId,
    deviceRole: payload.deviceRole ?? row.deviceRole,
    status: isKnownStatus ? status : row.status,
    progress: payload.percent ?? null,
    attempt: row.attempt ?? 1,
    errorStep: payload.step ?? null,
    errorMessage: payload.message ?? payload.err ?? null,
    speed: payload.speed ?? null,
    downloaded: payload.downloaded ?? null,
    total: payload.total ?? null,
    eventType,
    createdAt: payload.receivedAt ?? new Date().toISOString(),
  };
};

export const computeCampaignSummary = (devices = []) => {
  const { succeeded, failed, pending } = buildCampaignDeviceBuckets(devices);

  return {
    successCount: succeeded.length,
    failureCount: failed.length,
    pendingCount: pending.length,
    deviceCount: devices.length,
    status: pending.length > 0 ? "active" : "completed",
    succeeded,
    failed,
    pending,
  };
};

export const getDeviceCountBucket = (status) => {
  if (!status) return null;
  if (status === "success") return "success";
  if (FAILED_STATUSES.has(status)) return "failed";
  if (TERMINAL_STATUSES.has(status)) return "terminal";
  return "pending";
};

export const adjustCampaignCounts = (counts, oldStatus, newStatus) => {
  const oldBucket = getDeviceCountBucket(oldStatus);
  const newBucket = getDeviceCountBucket(newStatus);
  if (!newBucket || oldBucket === newBucket) return counts;

  const next = {
    successCount: counts.successCount ?? 0,
    failureCount: counts.failureCount ?? 0,
    pendingCount: counts.pendingCount ?? 0,
    deviceCount: counts.deviceCount ?? 0,
  };

  if (oldBucket === "success") next.successCount = Math.max(0, next.successCount - 1);
  else if (oldBucket === "failed") next.failureCount = Math.max(0, next.failureCount - 1);
  else if (oldBucket === "pending") next.pendingCount = Math.max(0, next.pendingCount - 1);

  if (newBucket === "success") next.successCount += 1;
  else if (newBucket === "failed") next.failureCount += 1;
  else if (newBucket === "pending") next.pendingCount += 1;

  return {
    ...next,
    status: next.pendingCount > 0 ? "active" : "completed",
  };
};

export const shouldAppendLiveEvent = (oldStatus, newStatus, payload) => {
  if (payload?.message && !KNOWN_LIVE_STATUSES.has(newStatus)) {
    return true;
  }
  if (!newStatus || oldStatus === newStatus) return false;
  if (newStatus === "downloading") return false;
  return true;
};

export const buildDeviceIndexMap = (devices = []) => {
  const map = {};
  devices.forEach((row, index) => {
    if (row?.mqttDeviceId) map[row.mqttDeviceId] = index;
  });
  return map;
};
